import { useLocalization } from "@fluent/react"
import AccountTreeIcon from "@mui/icons-material/AccountTree"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { ReactNode, useMemo } from "react"
import { StructureDefinitionDto } from "../structureSlice"

interface StructureDefinitionSelectProps {
    structures: StructureDefinitionDto[]
    value?: string | null
    onChange: (value: string | null, option: StructureDefinitionDto | null) => void
    label?: string
    placeholder?: string
    helperText?: ReactNode
    disabled?: boolean
    loading?: boolean
    size?: "small" | "medium"
}

export const StructureDefinitionSelect = ({
    structures,
    value,
    onChange,
    label,
    placeholder,
    helperText,
    disabled = false,
    loading = false,
    size = "small",
}: StructureDefinitionSelectProps) => {
    const { l10n } = useLocalization()

    const selected = useMemo(
        () => structures.find(structure => structure.id === value) ?? null,
        [structures, value],
    )

    return (
        <Autocomplete<StructureDefinitionDto, false, false, false>
            options={structures}
            value={selected}
            onChange={(_, option) => onChange(option?.id ?? null, option ?? null)}
            getOptionLabel={option => `${option.code || "-"} - ${option.name}`}
            isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
            disabled={disabled}
            loading={loading}
            size={size}
            renderOption={(props, option) => {
                return (
                    <Box component="li" {...props} sx={{ py: 1 }}>
                        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, width: "100%" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 72 }} noWrap>
                                {option.code || "-"}
                            </Typography>
                            <Typography variant="body2" noWrap>
                                {option.name}
                            </Typography>
                        </Box>
                    </Box>
                )
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label || l10n.getString("qms-templates-structures-title", null, "Struktura")}
                    placeholder={placeholder || l10n.getString("qms-templates-structures-placeholder", null, "Wybierz strukturę")}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <AccountTreeIcon fontSize="small" color="primary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    )
}